import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { orderApi, formatApiError } from '../lib/orderApi';
import { useAuth } from '../context/AuthContext';
import { redButton, goldButton } from '../components/PageShell';

const STATUS_LABELS = {
  paid: { text: 'Pagado', color: '#C9A84C' },
  preparing: { text: 'Preparando', color: '#C9A84C' },
  out_for_delivery: { text: 'En camino', color: '#E8C96A' },
  arriving_soon: { text: 'Llegando', color: '#E8C96A' },
  delivered: { text: 'Entregado', color: '#7FB77E' },
};

const inputStyle = {
  width: '100%', background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.3)',
  color: '#F5F5F0', padding: '13px 15px', fontSize: '15px', outline: 'none',
};

const card = { background: '#141414', border: '1px solid rgba(201,168,76,0.18)', padding: '20px' };

export default function DriverPortal() {
  const { user, loading, refreshUser, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const inviteId = searchParams.get('invite');

  const [step, setStep] = useState(inviteId ? 'code' : 'email');
  const [sessionId, setSessionId] = useState(inviteId || '');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [inviteName, setInviteName] = useState('');

  const [data, setData] = useState(null);
  const [routeBusy, setRouteBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState('');

  const isDriver = user && (user.role === 'driver' || user.role === 'admin');

  const loadOrders = useCallback(async () => {
    try {
      const { data: d } = await orderApi.get('/driver/orders');
      setData(d);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { if (isDriver) loadOrders(); }, [isDriver, loadOrders]);

  useEffect(() => {
    if (!inviteId || user) return;
    orderApi.get(`/driver/invite/${inviteId}`).then(({ data: d }) => {
      if (d.valid) { setInviteName(d.name); } else {
        setError('Esta invitación no es válida o expiró. Pide tu código con tu email.');
        setStep('email'); setSessionId('');
      }
    }).catch(() => {});
  }, [inviteId, user]);

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { data: d } = await orderApi.post('/driver/request-code', { email: email.trim() });
      setSessionId(d.session_id); setStep('code'); setCode('');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await orderApi.post('/driver/verify-code', { session_id: sessionId, code: code.trim() });
      if (inviteId) setSearchParams({});
      await refreshUser();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const openRoute = async () => {
    setRouteBusy(true); setError('');
    try {
      const { data: d } = await orderApi.post('/driver/route', {});
      window.open(d.maps_url, '_blank');
      loadOrders();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setRouteBusy(false); }
  };

  const changeStatus = async (orderId, status) => {
    setStatusBusy(orderId); setError('');
    try {
      await orderApi.patch(`/driver/orders/${orderId}/status`, { status });
      await loadOrders();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setStatusBusy(''); }
  };

  const doLogout = async () => {
    await logout();
    setStep('email'); setSessionId(''); setCode(''); setData(null);
  };

  const shell = (children) => (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#F5F5F0' }}>
      <header style={{ borderBottom: '1px solid rgba(201,168,76,0.18)', background: 'rgba(10,10,10,0.95)' }}>
        <div className="mx-auto max-w-[760px] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center" style={{ width: '34px', height: '34px', background: '#C8282B', borderRadius: '8px' }}>
              <span className="text-white font-bold" style={{ fontSize: '18px' }}>山</span>
            </span>
            <div>
              <div className="font-display" style={{ fontSize: '18px', fontWeight: 700 }}>Yamauchi</div>
              <div className="font-body uppercase" style={{ fontSize: '10px', letterSpacing: '2px', color: '#C9A84C' }}>Portal de Choferes</div>
            </div>
          </div>
          {isDriver && (
            <button onClick={doLogout} className="font-body uppercase text-[11px]" style={{ letterSpacing: '1.5px', color: '#C2C1B6', background: 'none', border: 'none', cursor: 'pointer' }} data-testid="driver-logout">
              Salir
            </button>
          )}
        </div>
      </header>
      <main className="mx-auto max-w-[760px] px-5 py-8">{children}</main>
    </div>
  );

  if (loading) return shell(<p className="font-body" style={{ fontSize: '14px', color: '#C2C1B6' }}>Cargando...</p>);

  // ---------- login ----------
  if (!isDriver) {
    return shell(
      <div className="mx-auto" style={{ maxWidth: '420px' }}>
        <h1 className="font-display" style={{ fontSize: '28px', fontWeight: 700 }}>
          {step === 'code' && inviteName ? `Hola, ${inviteName}` : 'Acceso de choferes'}
        </h1>
        <p className="font-body mt-2 mb-6" style={{ fontSize: '13px', color: '#C2C1B6' }}>
          {step === 'email'
            ? 'Escribe tu email y te enviaremos un código de acceso de 6 dígitos.'
            : 'Si tu email está registrado como chofer, recibirás un código de 6 dígitos. Escríbelo aquí.'}
        </p>
        {step === 'email' ? (
          <form onSubmit={requestCode}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" required style={inputStyle} data-testid="driver-email-input" />
            <button type="submit" disabled={busy} className="mt-4 w-full" style={{ ...redButton, opacity: busy ? 0.6 : 1 }} data-testid="driver-request-code">
              {busy ? 'Enviando…' : 'Enviar código'}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode}>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" inputMode="numeric" required
              style={{ ...inputStyle, textAlign: 'center', fontSize: '26px', letterSpacing: '10px', fontFamily: 'monospace' }} data-testid="driver-code-input" />
            <button type="submit" disabled={busy || code.length !== 6} className="mt-4 w-full" style={{ ...redButton, opacity: busy || code.length !== 6 ? 0.6 : 1 }} data-testid="driver-verify-code">
              {busy ? 'Verificando…' : 'Entrar'}
            </button>
            {!inviteId && (
              <button type="button" onClick={() => { setStep('email'); setError(''); }} className="mt-3 w-full font-body" style={{ background: 'none', border: 'none', color: '#C9A84C', fontSize: '12px', cursor: 'pointer' }} data-testid="driver-back-to-email">
                Usar otro email / pedir nuevo código
              </button>
            )}
          </form>
        )}
        {error && (
          <p className="font-body mt-4 p-3" style={{ fontSize: '13px', color: '#E0736F', background: 'rgba(200,40,43,0.12)', border: '1px solid rgba(200,40,43,0.35)' }} data-testid="driver-login-error">{error}</p>
        )}
      </div>
    );
  }

  // ---------- orders ----------
  const orders = data?.orders || [];
  const pending = data?.pending_count ?? 0;
  return shell(
    <div data-testid="driver-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display" style={{ fontSize: '26px', fontWeight: 700 }}>Entregas del día</h1>
          {data?.delivery_label && (
            <p className="font-body mt-1" style={{ fontSize: '13px', color: '#C9A84C' }} data-testid="driver-delivery-label">{data.delivery_label}</p>
          )}
          <p className="font-body mt-1" style={{ fontSize: '12px', color: '#C2C1B6' }} data-testid="driver-pending-count">
            {orders.length} pedido(s) · {pending} pendiente(s)
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={loadOrders} style={{ ...goldButton, padding: '11px 18px' }} data-testid="driver-refresh">Actualizar</button>
          <button onClick={openRoute} disabled={routeBusy || pending === 0} style={{ ...redButton, padding: '12px 20px', opacity: routeBusy || pending === 0 ? 0.6 : 1 }} data-testid="driver-route-btn">
            {routeBusy ? 'Creando…' : 'Ruta del día'}
          </button>
        </div>
      </div>

      {error && (
        <p className="font-body mt-4 p-3" style={{ fontSize: '13px', color: '#E0736F', background: 'rgba(200,40,43,0.12)', border: '1px solid rgba(200,40,43,0.35)' }} data-testid="driver-error">{error}</p>
      )}

      {orders.length === 0 && (
        <div className="mt-6" style={card} data-testid="driver-empty">
          <p className="font-body" style={{ fontSize: '14px', color: '#C2C1B6' }}>No hay pedidos para entregar todavía.</p>
        </div>
      )}

      <div className="mt-6 space-y-4" data-testid="driver-orders-list">
        {orders.map((o) => {
          const st = STATUS_LABELS[o.status] || { text: o.status, color: '#C2C1B6' };
          return (
            <div key={o.id} style={card} data-testid={`driver-order-${o.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="font-body" style={{ fontSize: '12px', color: '#888880', fontFamily: 'monospace' }}>#{o.id.slice(0, 8).toUpperCase()}</span>
                <span className="font-body uppercase" style={{ fontSize: '11px', letterSpacing: '1.5px', color: st.color, border: `1px solid ${st.color}`, padding: '3px 10px' }} data-testid={`driver-status-badge-${o.id}`}>
                  {st.text}
                </span>
              </div>
              <div className="mt-3">
                <p className="font-display" style={{ fontSize: '18px', fontWeight: 700 }}>{o.customer_name}</p>
                <p className="font-body mt-1" style={{ fontSize: '14px', color: '#F5F5F0' }}>{o.address}</p>
                {o.reference && <p className="font-body mt-1" style={{ fontSize: '13px', color: '#C2C1B6' }}>Referencia: {o.reference}</p>}
                {o.notes && <p className="font-body mt-1" style={{ fontSize: '13px', color: '#C9A84C' }}>Nota: {o.notes}</p>}
              </div>
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(201,168,76,0.15)' }}>
                {(o.items || []).map((it, i) => (
                  <div key={`${it.id}-${i}`} className="flex justify-between font-body" style={{ fontSize: '13px', color: '#C2C1B6', lineHeight: 1.9 }}>
                    <span>{it.qty}× {it.name}{it.modifiers && it.modifiers.length ? ` (${it.modifiers.join(', ')})` : ''}</span>
                  </div>
                ))}
                <div className="flex justify-between font-body mt-1" style={{ fontSize: '14px', fontWeight: 700 }}>
                  <span>Total</span><span>${(o.total || 0).toFixed(2)}</span>
                </div>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                {o.customer_phone && (
                  <a href={`tel:${o.customer_phone}`} className="font-body uppercase text-center" style={{ ...goldButton, padding: '11px 18px', textDecoration: 'none' }} data-testid={`driver-call-${o.id}`}>
                    📞 Llamar
                  </a>
                )}
                {['paid', 'preparing'].includes(o.status) && (
                  <button onClick={() => changeStatus(o.id, 'out_for_delivery')} disabled={statusBusy === o.id} style={{ ...redButton, padding: '12px 18px', opacity: statusBusy === o.id ? 0.6 : 1 }} data-testid={`driver-onway-${o.id}`}>
                    En camino
                  </button>
                )}
                {o.status === 'out_for_delivery' && (
                  <button onClick={() => changeStatus(o.id, 'arriving_soon')} disabled={statusBusy === o.id} style={{ ...goldButton, padding: '11px 18px', opacity: statusBusy === o.id ? 0.6 : 1 }} data-testid={`driver-arriving-${o.id}`}>
                    Llegando (~20 min)
                  </button>
                )}
                {['out_for_delivery', 'arriving_soon'].includes(o.status) && (
                  <button onClick={() => changeStatus(o.id, 'delivered')} disabled={statusBusy === o.id} style={{ ...redButton, padding: '12px 18px', opacity: statusBusy === o.id ? 0.6 : 1 }} data-testid={`driver-delivered-${o.id}`}>
                    ✓ Entregado
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
